import { useEffect, useState } from "react";
import { Modal } from "../shared/modal";
import Input from "../shared/input";
import { SubmitButton } from "../shared/buttons/submit-button";
import Typography from "../shared/typography";
import { useCreateDiagnosis } from "../../hooks/useCreateDiagnosis";

interface AddDiagnosisModalProps {
  userId?: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function AddDiagnosisModal({
  userId,
  isOpen,
  onClose,
}: AddDiagnosisModalProps) {
  const { create, isPending } = useCreateDiagnosis(userId);

  const [description, setDescription] = useState("");
  const [code, setCode] = useState("");
  const [notes, setNotes] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState<string>("");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (isOpen) {
      setError("");
    }
  }, [isOpen]);

  const resetForm = () => {
    setDescription("");
    setCode("");
    setNotes("");
    setEffectiveFrom("");
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!description.trim()) {
      setError("Description is required");
      return;
    }

    create(
      {
        diagnosis_description: description.trim(),
        diagnosis_code: code.trim() || undefined,
        notes: notes.trim() || undefined,
        effective_from: effectiveFrom || undefined,
        status: "active",
      },
      {
        onSuccess: () => {
          resetForm();
          onClose();
        },
      }
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Diagnosis">
      <form onSubmit={onSubmit} className="flex flex-col gap-4 w-80">
        <div className="space-y-1">
          <Input
            id="diagnosis-description"
            label="Description"
            placeholder="e.g., Hypertension"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
          />
          {error && (
            <Typography variant="caption" className="text-terracotta-600">
              {error}
            </Typography>
          )}
        </div>

        <Input
          id="diagnosis-code"
          label="Code (optional)"
          placeholder="e.g., I10"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          fullWidth
        />

        <div className="flex flex-col">
          <label htmlFor="diagnosis-notes" className="mb-1">
            <Typography variant="caption">Notes (optional)</Typography>
          </label>
          <textarea
            id="diagnosis-notes"
            className="border border-teal-100 hover:border-teal-200 rounded-md p-2 text-sm focus:outline-none focus:border-teal-500"
            rows={3}
            placeholder="Additional details"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <Input
          id="effective-from"
          label="Effective From (optional)"
          type="date"
          value={effectiveFrom}
          onChange={(e) => setEffectiveFrom(e.target.value)}
          fullWidth
        />

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            className="px-3 py-2 text-sm rounded-md border border-brown-300 text-brown-800 hover:bg-beige-100"
            onClick={() => {
              resetForm();
              onClose();
            }}
          >
            Cancel
          </button>
          <SubmitButton isLoading={isPending}>Save</SubmitButton>
        </div>
      </form>
    </Modal>
  );
}

